/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared DTOs for Admin Business Intelligence Analytics (Task 11.6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * 1. Analytics Period Filter
 */
export const AdminAnalyticsPeriodSchema = z
  .enum(['7d', '30d', '90d', '12m'])
  .default('30d')
  .openapi('AdminAnalyticsPeriod');

export type AdminAnalyticsPeriod = z.infer<typeof AdminAnalyticsPeriodSchema>;

export const AdminAnalyticsQuerySchema = z
  .object({
    period: AdminAnalyticsPeriodSchema.optional().default('30d'),
  })
  .openapi('AdminAnalyticsQuery');

export type AdminAnalyticsQuery = z.infer<typeof AdminAnalyticsQuerySchema>;

/**
 * 2. KPI Metrics Growth Indicators
 */
export const AdminGrowthRatesSchema = z
  .object({
    mrr: z.number().openapi({ example: 14.2, description: 'Percentage growth in MRR vs previous period' }),
    mau: z.number().openapi({ example: 8.5, description: 'Percentage growth in MAU vs previous period' }),
    workspaces: z.number().openapi({ example: 12.0, description: 'Percentage growth in Workspaces vs previous period' }),
    paidConversion: z.number().openapi({ example: 3.1, description: 'Percentage growth in Paid conversion rate' }),
  })
  .openapi('AdminGrowthRates');

export type AdminGrowthRates = z.infer<typeof AdminGrowthRatesSchema>;

/**
 * 3. Core KPI Summary
 */
export const AdminKpiMetricsSchema = z
  .object({
    mrr: z.number().openapi({ example: 24850, description: 'Monthly Recurring Revenue in USD' }),
    arr: z.number().openapi({ example: 298200, description: 'Annual Run Rate (MRR * 12) in USD' }),
    mau: z.number().openapi({ example: 1429, description: 'Monthly Active Users in last 30 days' }),
    totalWorkspaces: z.number().openapi({ example: 1850, description: 'Total workspaces created on edge' }),
    newWorkspaces: z.number().openapi({ example: 132, description: 'New workspaces created in this period' }),
    trialToPaidRate: z.number().openapi({ example: 18.5, description: 'Trial-to-paid conversion percentage' }),
    activeSubscriptions: z.number().openapi({ example: 642, description: 'Number of active subscriptions' }),
    churnRate: z.number().openapi({ example: 2.4, description: 'Subscription churn percentage' }),
    totalRevenue: z.number().openapi({ example: 89450, description: 'Total revenue collected in period' }),
    growthRates: AdminGrowthRatesSchema,
  })
  .openapi('AdminKpiMetrics');

export type AdminKpiMetrics = z.infer<typeof AdminKpiMetricsSchema>;

/**
 * 4. Revenue Timeline Data Point (Multi-Gateway Breakdown)
 */
export const AdminRevenueDataPointSchema = z
  .object({
    date: z.string().openapi({ example: '2026-10-01', description: 'Date or interval label' }),
    stripe: z.number().openapi({ example: 1250, description: 'Revenue collected via Stripe' }),
    paypal: z.number().openapi({ example: 620, description: 'Revenue collected via PayPal' }),
    bankTransfer: z.number().openapi({ example: 850, description: 'Revenue collected via Bank Transfer' }),
    total: z.number().openapi({ example: 2720, description: 'Total combined revenue on this date' }),
  })
  .openapi('AdminRevenueDataPoint');

export type AdminRevenueDataPoint = z.infer<typeof AdminRevenueDataPointSchema>;

/**
 * 5. Subscription Timeline Data Point (Churn vs Net Growth)
 */
export const AdminSubscriptionDataPointSchema = z
  .object({
    date: z.string().openapi({ example: '2026-10-01', description: 'Date or interval label' }),
    newSubscribers: z.number().openapi({ example: 14, description: 'Newly acquired subscriptions' }),
    churnedSubscribers: z.number().openapi({ example: 2, description: 'Canceled or expired subscriptions' }),
    activeSubscribers: z.number().openapi({ example: 640, description: 'Cumulative active subscribers' }),
    netGrowth: z.number().openapi({ example: 12, description: 'Net subscriber change (new - churned)' }),
  })
  .openapi('AdminSubscriptionDataPoint');

export type AdminSubscriptionDataPoint = z.infer<typeof AdminSubscriptionDataPointSchema>;

/**
 * 6. Payment Gateway Distribution Item
 */
export const AdminPaymentGatewayShareSchema = z
  .object({
    gateway: z.string().openapi({ example: 'stripe', description: 'Gateway code name' }),
    label: z.string().openapi({ example: 'Stripe Checkout', description: 'Human friendly gateway label' }),
    amount: z.number().openapi({ example: 45200, description: 'Total collected amount' }),
    count: z.number().openapi({ example: 380, description: 'Number of transactions' }),
    percentage: z.number().openapi({ example: 62.5, description: 'Percentage share of total revenue' }),
  })
  .openapi('AdminPaymentGatewayShare');

export type AdminPaymentGatewayShare = z.infer<typeof AdminPaymentGatewayShareSchema>;

/**
 * 7. Complete Admin Analytics Response
 */
export const AdminAnalyticsResponseSchema = z
  .object({
    success: z.boolean().default(true),
    period: AdminAnalyticsPeriodSchema,
    kpi: AdminKpiMetricsSchema,
    revenueTimeline: z.array(AdminRevenueDataPointSchema),
    subscriptionTimeline: z.array(AdminSubscriptionDataPointSchema),
    paymentGatewayDistribution: z.array(AdminPaymentGatewayShareSchema),
    generatedAt: z.string().openapi({ example: '2026-10-26T15:30:00Z', description: 'ISO Timestamp of calculation' }),
  })
  .openapi('AdminAnalyticsResponse');

export type AdminAnalyticsResponse = z.infer<typeof AdminAnalyticsResponseSchema>;
